import Link from "next/link"
import React from "react"
import { useToasts } from "react-toast-notifications"
import { WishlistAvailabilityItem } from "../../../../api/backend/backend-api"
import { toProduct } from "../../../../converters/catalog"
import { productPagePath } from "../../../../root/routing/urls"
import { useAppDispatch } from "../../../../state/store"
import { lightBlack } from "../../../../themes/variables"
import { ProductVariant } from "../../../../types/product"
import CustomButton from "../../../../ui/atoms/Button"
import { CloseButton } from "../../../../ui/atoms/IconButton/buttons/close"
import CustomPaper from "../../../../ui/molecules/CustomPaper"
import ProductLineItem from "../../../../ui/organisms/Product/ProductLineItem"
import { handleAddToBasketResult } from "../../../checkout/handlers/addToBasketHandler"
import { addToBasket } from "../../../checkout/state/actions/addToBasket"
import { Basket } from "../../../checkout/state/checkoutTypes"
import { LineItemInput } from "../../../checkout/types"
import { useLabels } from "../../../contents/hooks"
import { removeItemFromUserWishlist } from "../../state/actions/removeItemFromWishlist"
import classes from "./WishlistItem.module.css"

interface Props {
  item: WishlistAvailabilityItem
  onAddedToBasket?: () => void
}

const WishlistItem = ({ item, onAddedToBasket }: Props) => {
  const { getLabel } = useLabels()
  const { addToast } = useToasts()
  const product = toProduct(item.item)
  const variant = product.variants.find(
    (x) => x.info.variantId === item.variantId
  )
  const dispatch = useAppDispatch()
  const handleRemove = () =>
    dispatch(removeItemFromUserWishlist(item.variantId))

  const [updatingBasket, setUpdatingBasket] = React.useState(false)

  const handleAddToBasket = () => {
    const item: LineItemInput = {
      product,
      productVariant: variant as ProductVariant,
      quantity: 1,
      source: "wishlist",
    }
    setUpdatingBasket(true)
    dispatch(
      addToBasket(item, (basket: Basket) => {
        handleAddToBasketResult({
          inputItem: item,
          outputBasket: basket,
          addedCallback: () => {
            setUpdatingBasket(false)
            onAddedToBasket?.()
          },
          notAddedCallback: () => {
            setUpdatingBasket(false)
            addToast(getLabel("basket", "product-not-added"), {
              autoDismiss: true,
              appearance: "warning",
            })
          },
        })
      })
    )
  }

  if (!variant) {
    return <></>
  }

  return (
    <Link href={productPagePath(variant.info.slug ?? product.info.slug)}>
      <div>
        <CustomPaper className="py-2 pr-2" clickable>
          <ProductLineItem
            product={product}
            variant={variant}
            pricing={{
              originalPrice: variant.inventory?.price as number,
              compareAtPrice: variant.inventory?.compareAtPrice,
            }}
            topControls={
              <CloseButton
                color={lightBlack}
                onClick={handleRemove}
                size={20}
              />
            }
          />
          <div className="mt-2 text-right">
            {variant.inventory?.available && variant.inventory?.price > 0 ? (
              <CustomButton
                loading={updatingBasket}
                onClick={handleAddToBasket}
                className={classes.button}
                preventDefault
              >
                {getLabel("basket", "add-to-basket")}
              </CustomButton>
            ) : (
              <CustomButton className={classes.button} disabled>
                {getLabel("product", "out-of-stock")}
              </CustomButton>
            )}
          </div>
        </CustomPaper>
      </div>
    </Link>
  )
}

export default WishlistItem
